/**
 * rondo-web-forms
 *
 * Rondo Web Forms
 *
 * @author Jiri Hybek <jiri@hybek.cz>
 * @license See LICENSE file distributed with this source code.
 */

import { FormRepository } from "../../shared/FormRepository";
import { IFormData } from "../../shared/FormModel/IFormData";
import { FormModel } from "../../shared/FormModel/FormModel";
import { EventEmitter } from "../../shared/EventEmitter";

const SESSION_STORAGE_OPENED_DOC_KEY = "rondo_opened_docs";

/**
 * Interface of a document stored in a session store
 */
interface ISessionDocumentData extends IFormData {
	modified: boolean;
}

/**
 * Manages opened documents
 */
export class DocumentManager extends EventEmitter<"update"|"close"> {

	/** Opened documents indx */
	private docIndex: { [K: string]: FormModel } = {};

	/** Opened document as a list */
	private docList: Array<FormModel> = [];

	/** If some of the documents were modified */
	private docsModified: boolean = false;

	/**
	 * Manager constructor
	 *
	 * @param formRepo Form reposiotory
	 */
	public constructor(
		private formRepo: FormRepository
	) {

		super();

		this.handleDocumentUpdate = this.handleDocumentUpdate.bind(this);

		setInterval(() => this.handleStoreTimeTick(), 3000);

	}

	/**
	 * Opens a document
	 *
	 * @param formId Form ID
	 * @param formVersion Form version
	 * @param data Form data (optional)
	 */
	public open(formId: string, formVersion: string, data?: IFormData): string {

		// Materialize form
		const form = this.formRepo.materialize(formId, formVersion);

		// Set data
		if (data) {
			form.setData(data, true);
		} else {
			form.update(true);
		}

		// Prevent opening a document twice
		if (this.docIndex[form.getId()] !== undefined) {
			return form.getId();
		}

		// Add to lists
		this.docIndex[form.getId()] = form;
		this.docList.push(form);

		form.on("change", this.handleDocumentUpdate);
		this.docsModified = true;

		this.emit("update");

		return form.getId();

	}

	/**
	 * Returns a document by ID
	 *
	 * @param docId Document ID
	 */
	public get(docId: string) {

		return this.docIndex[docId];

	}

	/**
	 * Returns opened documents as an array
	 */
	public getList() {

		return this.docList;

	}

	/**
	 * Closes a document by ID
	 *
	 * @param docId Document ID
	 */
	public close(docId: string) {

		if (!this.docIndex[docId]) {
			return;
		}

		const form = this.docIndex[docId];
		const index = this.docList.indexOf(form);

		form.off("change", this.handleDocumentUpdate);

		this.docList.splice(index, 1);
		delete this.docIndex[docId];

		let nextId = null;
		const nextIndex = Math.max(0, index - 1);

		if (this.docList[nextIndex]) {
			nextId = this.docList[nextIndex].getId();
		}

		this.docsModified = true;

		this.emit("update");
		this.emit("close", { docId: docId, nextDocId: nextId });

	}

	/**
	 * Stored currently opened documents into local storage
	 */
	public storeSession() {

		console.debug("[DocumentManager] Storing session...");

		const state: Array<ISessionDocumentData> = this.docList.map((item) => ({
			...item.getData(),
			modified: item.isModified()
		}));

		localStorage.setItem(SESSION_STORAGE_OPENED_DOC_KEY, JSON.stringify(state));

		console.debug("[DocumentManager] Session stored.");

	}

	/**
	 * Restores opened documents from a local storage
	 */
	public restoreSession() {

		console.debug("[DocumentManager] Restoring session...");

		const dataStr = localStorage.getItem(SESSION_STORAGE_OPENED_DOC_KEY);
		let data: Array<ISessionDocumentData>;

		if (!dataStr) {
			return;
		}

		try {
			data = JSON.parse(dataStr);
		} catch(err) {
			console.error("Failed to restore session documents, JSON parse error: " + String(err));
			return;
		}

		for (let i = 0; i < data.length;i++) {

			const item = data[i];

			try {

				this.open(item.formId, item.formVersion, item);

				if (item.modified) {
					this.docIndex[item.id].setModified(true);
				}

			} catch (err) {

				console.warn("Failed to restore document from session:", item, err);

			}

		}

		console.debug("[DocumentManager] Session restored.");

	}

	/**
	 * Handles document update event
	 */
	public handleDocumentUpdate() {

		this.docsModified = true;

	}

	/**
	 * Handles store timer
	 */
	public handleStoreTimeTick() {

		if (!this.docsModified) {
			return;
		}

		this.storeSession();
		this.docsModified = false;

	}

}
